import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import yaml from "js-yaml";
import { z } from "zod";
import {
  TaskProfile,
  TaskType,
  ModelPricing,
  OverheadConfig,
  AgentRecommendation,
  Complexity,
} from "./schemas.js";

// ── YAML file schemas ───────────────────────────────────────

const ModelsFile = z.object({
  models: z.array(ModelPricing),
});

const TaskProfilesFile = z.object({
  profiles: z.record(z.string(), TaskProfile),
  complexity_multipliers: z.record(z.string(), z.number()),
  overhead_multipliers: OverheadConfig,
});

const AgentsFile = z.object({
  recommendations: z.record(z.string(), AgentRecommendation),
});

// ── Loader functions ────────────────────────────────────────

function loadYaml<T>(filePath: string, schema: z.ZodType<T>): T {
  const content = readFileSync(filePath, "utf-8");
  const raw = yaml.load(content, { schema: yaml.JSON_SCHEMA });
  return schema.parse(raw);
}

function findDataDir(): string {
  const base = import.meta.dirname ?? __dirname;
  // Try bundled location first (dist/data/ when installed from npm)
  const bundled = resolve(base, "data");
  try {
    readFileSync(resolve(bundled, "models.yaml"), "utf-8");
    return bundled;
  } catch {
    // Fall back to monorepo location (../../../data from packages/core/dist/)
    return resolve(base, "../../../data");
  }
}

export interface LoadedData {
  models: ModelPricing[];
  profiles: Record<string, TaskProfile>;
  complexityMultipliers: Record<string, number>;
  overhead: OverheadConfig;
  recommendations: Record<string, AgentRecommendation>;
}

// ── Cache ───────────────────────────────────────────────────

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const cacheMap = new Map<string, { data: LoadedData; timestamp: number }>();

export function clearCache(): void {
  cacheMap.clear();
}

// ── Public API ──────────────────────────────────────────────

export function loadData(dataDir?: string, ttlMs?: number, projectDir?: string): LoadedData {
  const dir = dataDir ?? findDataDir();
  const resolvedDir = resolve(dir);
  const ttl = ttlMs ?? DEFAULT_TTL_MS;

  // Determine if calibrated profiles exist for cache key differentiation
  const calibratedPath = resolve(projectDir ?? process.cwd(), ".fathom", "calibrated-profiles.yaml");
  const hasCalibrated = existsSync(calibratedPath);
  const cacheKey = hasCalibrated ? resolvedDir + "+calibrated" : resolvedDir;

  // Check cache (ttl=0 bypasses)
  if (ttl > 0) {
    const cached = cacheMap.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
  }

  const modelsFile = loadYaml(resolve(dir, "models.yaml"), ModelsFile);
  const profilesFile = loadYaml(resolve(dir, "task-profiles.yaml"), TaskProfilesFile);
  const agentsFile = loadYaml(resolve(dir, "agents.yaml"), AgentsFile);

  const profiles = { ...profilesFile.profiles };

  // Merge calibrated profiles if available
  if (hasCalibrated) {
    try {
      const raw = yaml.load(readFileSync(calibratedPath, "utf-8"), { schema: yaml.JSON_SCHEMA });
      if (raw && typeof raw === "object" && "profiles" in raw) {
        const calibratedProfiles = (raw as { profiles: Record<string, unknown> }).profiles;
        if (calibratedProfiles && typeof calibratedProfiles === "object") {
          for (const [key, calibrated] of Object.entries(calibratedProfiles)) {
            if (profiles[key] && calibrated && typeof calibrated === "object") {
              const c = calibrated as Record<string, unknown>;
              if (typeof c.input === "number" && typeof c.output === "number" && typeof c.total === "number") {
                profiles[key] = { ...profiles[key], ...c as { input: number; output: number; total: number; notes?: string } };
              }
            }
          }
        }
      }
    } catch {
      // Silently ignore invalid calibrated profiles — fall back to defaults
    }
  }

  const data: LoadedData = {
    models: modelsFile.models,
    profiles,
    complexityMultipliers: profilesFile.complexity_multipliers,
    overhead: profilesFile.overhead_multipliers,
    recommendations: agentsFile.recommendations,
  };

  // Store in cache (even if ttl=0, we skip on read)
  if (ttl > 0) {
    cacheMap.set(cacheKey, { data, timestamp: Date.now() });
  }

  return data;
}

export function getProfile(
  profiles: Record<string, TaskProfile>,
  taskType: string,
): TaskProfile | undefined {
  return profiles[taskType];
}

export function getModelPricing(models: ModelPricing[], modelId: string): ModelPricing | undefined {
  return models.find((m) => m.id === modelId);
}

export function getComplexityMultiplier(
  multipliers: Record<string, number>,
  complexity: string,
): number {
  return multipliers[complexity] ?? 1.0;
}
